import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { useApi } from '../lib/api';
import { MultiSelectDropdown } from './MultiSelectDropdown';
import { OpportunityTimeline } from './OpportunityTimeline';
import type { ApiResponse, Opportunity, PicklistItem, User, CrmContactSuggestion } from '../types';

// Chargées à la demande : n'ouvrent que sur un clic explicite du bouton "+",
// pas besoin d'alourdir le bundle initial de la modale avant-vente (ouverte
// bien plus souvent) avec ce code rarement utilisé.
const CreateClientModal = lazy(() => import('./CreateClientModal').then((m) => ({ default: m.CreateClientModal })));
const CreateContactModal = lazy(() => import('./CreateContactModal').then((m) => ({ default: m.CreateContactModal })));

interface OpportunityModalProps {
    opportunity: Opportunity | null;
    onClose: () => void;
}

function ModalLoader() {
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
        </div>
    );
}

function usePicklist(category: string) {
    const api = useApi();
    return useQuery({
        queryKey: ['picklists', category],
        queryFn: () => api.get<ApiResponse<PicklistItem[]>>(`/api/picklists/${category}`),
    });
}

export function OpportunityModal({ opportunity, onClose }: OpportunityModalProps) {
    const api = useApi();
    const queryClient = useQueryClient();
    const isEditing = !!opportunity;

    const { data: statuses } = usePicklist('status');
    const { data: practices } = usePicklist('practice');
    // Owner/Consultant : uniquement des personnes déjà connectées à l'outil.
    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: () => api.get<ApiResponse<User[]>>('/api/users'),
    });
    // Owner et Porteur d'affaires sont attribués comme owner d'un enregistrement
    // Dynamics : un utilisateur sans licence CRM valide fait échouer toute la
    // sauvegarde de l'avant-vente si on le choisit. On ne propose donc ici que
    // les utilisateurs Clippit AVV actifs qui ont aussi un compte Dynamics
    // actif/licencié (cf. GET /api/crm/eligible-owners, fail-open côté API si
    // le CRM est injoignable). Sans rapport avec Consultant, qui n'est jamais
    // synchronisé vers le CRM et continue d'utiliser `users` sans filtre.
    const { data: eligibleOwners } = useQuery({
        queryKey: ['crm-eligible-owners'],
        queryFn: () => api.get<ApiResponse<User[]>>('/api/crm/eligible-owners'),
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
    // Clients déjà saisis, pour proposer une autocomplétion et éviter les
    // doublons d'orthographe — le champ reste libre pour un premier client.
    const { data: existingClients } = useQuery({
        queryKey: ['opportunity-clients'],
        queryFn: () => api.get<ApiResponse<string[]>>('/api/opportunities/clients'),
    });
    // Comptes actifs du CRM Dynamics 365 — en échec silencieux (retry: false)
    // si le CRM est injoignable/mal configuré : le champ reste utilisable avec
    // les seuls clients déjà saisis dans l'outil dans ce cas. staleTime évite
    // de rappeler le CRM (jusqu'à 500 comptes) à chaque ouverture de la
    // modale : un nouveau compte créé ailleurs peut mettre jusqu'à 5 minutes
    // à apparaître ici (sans rapport avec la création via le bouton "+", qui
    // invalide ce cache immédiatement).
    const { data: crmClients } = useQuery({
        queryKey: ['crm-clients'],
        queryFn: () => api.get<ApiResponse<string[]>>('/api/crm/clients'),
        retry: false,
        staleTime: 5 * 60 * 1000,
    });
    const clientSuggestions = Array.from(new Set([
        ...(crmClients?.data || []),
        ...(existingClients?.data || []),
    ])).sort((a, b) => a.localeCompare(b));

    const [form, setForm] = useState({
        client: opportunity?.client || '',
        name: opportunity?.name || '',
        description: opportunity?.description || '',
        statusId: opportunity?.statusId || '',
        practiceIds: opportunity?.practices.map((p) => p.id) || [],
        ownerId: opportunity?.ownerId || '',
        porteurAffairesId: opportunity?.porteurAffairesId || '',
        contactName: opportunity?.contactName || '',
        consultantIds: opportunity?.consultants.map((c) => c.id) || [],
        restitutionDate: opportunity?.restitutionDate?.slice(0, 10) || '',
        estimatedDays: opportunity?.estimatedDays != null ? String(opportunity.estimatedDays) : '',
        estimatedRevenue: opportunity?.estimatedRevenue != null ? String(opportunity.estimatedRevenue) : '',
        createTeamsFolder: false,
    });
    const [error, setError] = useState<{ message: string; details?: string } | null>(null);
    const [showCreateClient, setShowCreateClient] = useState(false);
    const [showCreateContact, setShowCreateContact] = useState(false);
    const [contactDropdownOpen, setContactDropdownOpen] = useState(false);
    const contactFieldRef = useRef<HTMLDivElement>(null);

    // Contacts Dynamics rattachés au compte du client déjà saisi (avec leur
    // email, pour désambiguïser les homonymes) — le champ Contact n'a de sens
    // qu'une fois un client choisi. Échec silencieux (retry: false) comme
    // pour crmClients.
    const { data: crmContacts } = useQuery({
        queryKey: ['crm-contacts', form.client],
        queryFn: () => api.get<ApiResponse<CrmContactSuggestion[]>>('/api/crm/contacts', { client: form.client }),
        enabled: !!form.client.trim(),
        retry: false,
    });
    const contactSuggestions = (crmContacts?.data || []).slice().sort((a, b) => a.name.localeCompare(b.name));
    const filteredContactSuggestions = contactSuggestions.filter((c) =>
        !form.contactName.trim() || c.name.toLowerCase().includes(form.contactName.trim().toLowerCase())
    );

    // Gagné/Perdu ne se décident plus dans l'outil : c'est le CRM qui fait foi,
    // Clippit AVV se contente de refléter la clôture décidée dans Dynamics
    // (cf. crmOutcomeSync.service.ts côté backend). "Sans suite" reste
    // sélectionnable ici : Dynamics n'a que Gagné/Perdu, cette nuance n'existe
    // que dans l'outil.
    const currentStatus = statuses?.data?.find((s) => s.id === form.statusId);
    const statusLockedByCrm = currentStatus?.outcome === 'won' || currentStatus?.outcome === 'lost';
    const selectableStatuses = (statuses?.data || []).filter(
        (s) => (s.outcome !== 'won' && s.outcome !== 'lost') || s.id === form.statusId
    );

    useEffect(() => {
        if (!contactDropdownOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (contactFieldRef.current && !contactFieldRef.current.contains(e.target as Node)) {
                setContactDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [contactDropdownOpen]);

    // Pré-sélectionne le premier statut disponible pour une nouvelle avant-vente
    // — un statut ouvert de préférence, Gagné/Perdu n'étant plus sélectionnables.
    useEffect(() => {
        if (!isEditing && !form.statusId && statuses?.data?.length) {
            const firstOpen = statuses.data.find((s) => s.outcome === 'open');
            setForm((f) => ({ ...f, statusId: (firstOpen || statuses.data[0]).id }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statuses]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const mutation = useMutation({
        mutationFn: () => {
            const payload = {
                client: form.client,
                name: form.name,
                description: form.description || undefined,
                statusId: form.statusId,
                practiceIds: form.practiceIds,
                ownerId: form.ownerId,
                porteurAffairesId: form.porteurAffairesId || null,
                contactName: form.contactName || null,
                consultantIds: form.consultantIds,
                restitutionDate: form.restitutionDate || null,
                estimatedDays: form.estimatedDays ? Number(form.estimatedDays) : null,
                estimatedRevenue: form.estimatedRevenue ? Number(form.estimatedRevenue) : null,
                ...(!isEditing && { createTeamsFolder: form.createTeamsFolder }),
            };
            return isEditing
                ? api.put<ApiResponse<Opportunity>>(`/api/opportunities/${opportunity!.id}`, payload)
                : api.post<ApiResponse<Opportunity>>('/api/opportunities', payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['opportunities'] });
            queryClient.invalidateQueries({ queryKey: ['opportunity-clients'] });
            onClose();
        },
        onError: (err: any) => setError({ message: err.message || 'Une erreur est survenue', details: err.details }),
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!form.client.trim() || !form.name.trim() || !form.ownerId || !form.statusId) {
            setError({ message: 'Client, nom, status et owner sont obligatoires' });
            return;
        }
        mutation.mutate();
    };

    const renderPicklistSelect = (
        label: string,
        value: string,
        onChange: (v: string) => void,
        items: PicklistItem[] | undefined,
        required = false,
        disabled = false,
        helperText?: string
    ) => (
        <div>
            <label className="field-label">{label}{required && ' *'}</label>
            <select className="input" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
                <option value="">—</option>
                {(items || []).map((item) => (
                    <option key={item.id} value={item.id}>{item.value}</option>
                ))}
            </select>
            {helperText && <p className="text-xs text-[var(--color-text-muted)] mt-1">{helperText}</p>}
        </div>
    );

    const renderUserSelect = (
        label: string,
        value: string,
        onChange: (v: string) => void,
        required = false,
        emptyLabel = '—',
        optionList: User[] = users?.data || []
    ) => {
        // Garantit que la valeur déjà enregistrée reste visible même si elle a
        // disparu d'optionList (ex: un owner qui a perdu sa licence Dynamics
        // depuis) — pour ne jamais changer silencieusement l'owner d'une
        // avant-vente existante à la simple ouverture du formulaire.
        const current = users?.data?.find((u) => u.id === value);
        const options = current && !optionList.some((u) => u.id === value)
            ? [...optionList, current]
            : optionList;
        return (
            <div>
                <label className="field-label">{label}{required && ' *'}</label>
                <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
                    <option value="">{emptyLabel}</option>
                    {options.map((u) => (
                        <option key={u.id} value={u.id}>{u.displayName}</option>
                    ))}
                </select>
            </div>
        );
    };

    const toggleConsultant = (id: string) => {
        setForm((f) => ({
            ...f,
            consultantIds: f.consultantIds.includes(id)
                ? f.consultantIds.filter((c) => c !== id)
                : [...f.consultantIds, id],
        }));
    };

    const togglePractice = (id: string) => {
        setForm((f) => ({
            ...f,
            practiceIds: f.practiceIds.includes(id)
                ? f.practiceIds.filter((p) => p !== id)
                : [...f.practiceIds, id],
        }));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-[var(--color-text)]">
                        {isEditing ? "Modifier l'avant-vente" : 'Nouvelle avant-vente'}
                    </h2>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-[var(--color-bg-hover)]">
                        <X className="w-5 h-5 text-[var(--color-text-muted)]" />
                    </button>
                </div>

                {error && (
                    <div className="text-sm text-[var(--color-error)] bg-[var(--color-error-light)] rounded-lg px-3 py-2 mb-4">
                        <p>{error.message}</p>
                        {error.details && (
                            <p className="text-xs opacity-75 mt-1">{error.details}</p>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="field-label">Client *</label>
                            <div className="flex gap-2">
                                <input
                                    className="input"
                                    value={form.client}
                                    list="client-suggestions"
                                    placeholder="Rechercher un client"
                                    onChange={(e) => setForm({ ...form, client: e.target.value })}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCreateClient(true)}
                                    className="btn-secondary flex-shrink-0 px-2.5"
                                    title="Créer un client dans le CRM"
                                >
                                    <UserPlus className="w-4 h-4" />
                                </button>
                            </div>
                            <datalist id="client-suggestions">
                                {clientSuggestions.map((c) => <option key={c} value={c} />)}
                            </datalist>
                        </div>
                        <div>
                            <label className="field-label">Nom *</label>
                            <input className="input" value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </div>
                    </div>

                    <div>
                        <label className="field-label">Description</label>
                        <textarea className="input" rows={2} value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        {renderPicklistSelect(
                            'Status', form.statusId, (v) => setForm({ ...form, statusId: v }), selectableStatuses, true,
                            statusLockedByCrm,
                            statusLockedByCrm ? 'Statut synchronisé depuis le CRM, non modifiable ici.' : undefined
                        )}
                        {renderUserSelect('Owner', form.ownerId, (v) => setForm({ ...form, ownerId: v }), true, '—', eligibleOwners?.data || users?.data || [])}
                    </div>

                    <div>
                        <label className="field-label">Contact</label>
                        <div className="flex gap-2">
                            <div ref={contactFieldRef} className="relative flex-1">
                                <input
                                    className="input w-full"
                                    value={form.contactName}
                                    placeholder={form.client.trim() ? 'Rechercher un contact' : 'Choisissez un client d\'abord'}
                                    disabled={!form.client.trim()}
                                    onFocus={() => setContactDropdownOpen(true)}
                                    onChange={(e) => { setForm({ ...form, contactName: e.target.value }); setContactDropdownOpen(true); }}
                                />
                                {contactDropdownOpen && form.client.trim() && (
                                    <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto card p-1 shadow-lg">
                                        {filteredContactSuggestions.length === 0 ? (
                                            <p className="text-sm text-[var(--color-text-muted)] px-2 py-1.5">Aucun contact trouvé</p>
                                        ) : (
                                            filteredContactSuggestions.map((c) => (
                                                <button
                                                    key={c.name}
                                                    type="button"
                                                    onClick={() => {
                                                        setForm((f) => ({ ...f, contactName: c.name }));
                                                        setContactDropdownOpen(false);
                                                    }}
                                                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-[var(--color-bg-hover)]"
                                                >
                                                    <span className="block text-sm text-[var(--color-text)] truncate">{c.name}</span>
                                                    {c.email && (
                                                        <span className="block text-xs text-[var(--color-text-muted)] truncate">{c.email}</span>
                                                    )}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCreateContact(true)}
                                disabled={!form.client.trim()}
                                className="btn-secondary flex-shrink-0 px-2.5"
                                title="Créer un contact dans le CRM"
                            >
                                <UserPlus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Contrairement à Owner, le champ Dynamics "Apporteur d'affaires" est un
                        simple lookup (pas le propriétaire de l'enregistrement) : Dynamics
                        n'exige pas de rôle de sécurité pour y référencer un utilisateur, donc
                        pas besoin de restreindre à la liste CRM-éligible ici. */}
                    {renderUserSelect('Porteur d\'affaires', form.porteurAffairesId, (v) => setForm({ ...form, porteurAffairesId: v }), false, 'N/A')}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="field-label">Date de restitution</label>
                            <input type="date" className="input" value={form.restitutionDate}
                                onChange={(e) => setForm({ ...form, restitutionDate: e.target.value })} />
                        </div>
                        <div>
                            <label className="field-label">Jours estimés</label>
                            <input type="number" min={1} step={1} className="input" value={form.estimatedDays}
                                onChange={(e) => setForm({ ...form, estimatedDays: e.target.value })} />
                        </div>
                    </div>

                    <div>
                        <label className="field-label">Estimation CA (€)</label>
                        <input type="number" min={0} step={1} className="input" value={form.estimatedRevenue}
                            onChange={(e) => setForm({ ...form, estimatedRevenue: e.target.value })} />
                    </div>

                    <MultiSelectDropdown
                        label="Practices"
                        options={(practices?.data || []).map((p) => ({ id: p.id, label: p.value }))}
                        selectedIds={form.practiceIds}
                        onToggle={togglePractice}
                        placeholder="Aucune"
                        emptyLabel="Aucune practice disponible"
                    />

                    <MultiSelectDropdown
                        label="Consultants"
                        options={(users?.data || []).map((u) => ({ id: u.id, label: u.displayName }))}
                        selectedIds={form.consultantIds}
                        onToggle={toggleConsultant}
                        placeholder="Aucun"
                        emptyLabel="Aucun utilisateur disponible"
                    />

                    {!isEditing && (
                        <label className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                            <input
                                type="checkbox"
                                checked={form.createTeamsFolder}
                                onChange={(e) => setForm({ ...form, createTeamsFolder: e.target.checked })}
                            />
                            Création du dossier Av-vente Teams
                        </label>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
                        <button type="submit" disabled={mutation.isPending} className="btn-primary">
                            {isEditing ? 'Enregistrer' : 'Créer'}
                        </button>
                    </div>
                </form>

                {isEditing && opportunity?.crmOpportunityId && (
                    <OpportunityTimeline opportunityId={opportunity.id} />
                )}
            </div>

            {showCreateClient && (
                <Suspense fallback={<ModalLoader />}>
                    <CreateClientModal
                        initialQuery={form.client}
                        onClose={() => setShowCreateClient(false)}
                        onCreated={(clientName) => {
                            setForm((f) => ({ ...f, client: clientName }));
                            setShowCreateClient(false);
                        }}
                    />
                </Suspense>
            )}

            {showCreateContact && (
                <Suspense fallback={<ModalLoader />}>
                    <CreateContactModal
                        accountName={form.client}
                        initialQuery={form.contactName}
                        onClose={() => setShowCreateContact(false)}
                        onCreated={(contactName) => {
                            setForm((f) => ({ ...f, contactName }));
                            setShowCreateContact(false);
                        }}
                    />
                </Suspense>
            )}
        </div>
    );
}
